import React, { useEffect, useRef } from 'react'

const InstancesList = ({ instances, currentTime, isLoading }) => {
  const listRef = useRef(null)
  const firstFutureEventRef = useRef(null)

  // Check if instance has passed
  const hasInstancePassed = instance => {
    const now = new Date(currentTime)

    if (instance.end_time) {
      const endTime = new Date(instance.end_time)
      return endTime < now
    } else {
      // If no end_time, consider it passed 1 hour after start
      const startTime = new Date(instance.start)
      const oneHourAfterStart = new Date(startTime.getTime() + 60 * 60 * 1000)
      return oneHourAfterStart < now
    }
  }

  // Scroll to first future event on mount or when instances change
  useEffect(() => {
    if (firstFutureEventRef.current && listRef.current) {
      firstFutureEventRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, [instances])

  // Format time from UTC to UK timezone
  const formatTime = instanceStart => {
    try {
      const utcDate = new Date(instanceStart)
      if (isNaN(utcDate.getTime())) {
        return ''
      }

      return utcDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/London'
      })
    } catch (error) {
      console.error('Error formatting time:', error)
      return ''
    }
  }

  // Get event image URL
  const getEventImageUrl = event => {
    if (event.image_urls?.standard_image?.variants?.square?.medium) {
      return event.image_urls.standard_image.variants.square.medium
    }
    if (event.image_urls?.default) {
      return event.image_urls.default
    }
    if (event.image_urls?.standard_image?.original) {
      return event.image_urls.standard_image.original
    }
    return null
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <div className='text-4xl text-gray-400'>Loading events...</div>
        </div>
      </div>
    )
  }

  if (!instances || instances.length === 0) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <div className='text-4xl text-gray-400 mb-4'>No events today</div>
        </div>
      </div>
    )
  }

  // Find index of first future event
  const firstFutureIndex = instances.findIndex(
    instance => !hasInstancePassed(instance)
  )

  return (
    <div className='h-full flex flex-col'>
      <h2 className='text-4xl font-bold px-8 pt-8 pb-6 bg-black sticky top-0 z-10'>
        Today's Events
      </h2>
      <div ref={listRef} className='flex-1 overflow-y-auto px-8 pb-8'>
        <div className='grid grid-cols-2 gap-6'>
          {instances.map((instance, index) => {
            const imageUrl = getEventImageUrl(instance.event)
            const isFreeOfCharge = instance.free_of_charge === true
            const isNotOnSale = !instance.on_sale || instance.locked
            const isSoldOut =
              instance.available === 0 && !isFreeOfCharge && !isNotOnSale
            const isLimitedAvailability = instance.limited_availability === true
            const isPast = hasInstancePassed(instance)
            const isFirstFuture = index === firstFutureIndex

            return (
              <div
                key={instance.id}
                ref={isFirstFuture ? firstFutureEventRef : null}
                className='flex gap-4 p-4 bg-gray-900 dark:bg-gray-800 rounded-lg border border-gray-700 transition-opacity'
                style={{ opacity: isPast ? 0.3 : 1 }}
              >
                {/* Event Image */}
                <div className='relative shrink-0'>
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={instance.event.name}
                      className='w-32 h-32 object-cover rounded-lg'
                    />
                  ) : (
                    <div className='w-32 h-32 bg-gray-700 rounded-lg flex items-center justify-center'>
                      <svg
                        className='w-16 h-16 text-gray-500'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                        />
                      </svg>
                    </div>
                  )}

                  {/* Status badges */}
                  {isFreeOfCharge && (
                    <div className='absolute top-2 right-2'>
                      <span className='bg-green-600 text-white text-xs font-bold px-2 py-1 rounded'>
                        FREE
                      </span>
                    </div>
                  )}
                  {!isFreeOfCharge && isSoldOut && (
                    <div className='absolute top-2 right-2'>
                      <span className='bg-red-600 text-white text-xs font-bold px-2 py-1 rounded'>
                        SOLD OUT
                      </span>
                    </div>
                  )}
                  {!isFreeOfCharge && !isSoldOut && isLimitedAvailability && (
                    <div className='absolute top-2 right-2'>
                      <span className='bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded'>
                        LIMITED
                      </span>
                    </div>
                  )}
                </div>

                {/* Event Details */}
                <div className='flex-grow flex flex-col justify-center min-w-0'>
                  <div className='text-3xl font-bold font-sans text-blue-400 mb-2'>
                    {formatTime(instance.start)}
                  </div>
                  <h3 className='text-2xl font-bold font-serif mb-1 line-clamp-2'>
                    {instance.event.name}
                  </h3>
                  {(instance.location || instance.venue?.name) && (
                    <div className='text-xl text-gray-400 mb-1'>
                      {instance.location || instance.venue.name}
                    </div>
                  )}
                  {instance.event.sub_title && (
                    <p className='text-lg text-gray-400 line-clamp-1'>
                      {instance.event.sub_title}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default InstancesList
